var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useIntl, FormattedMessage } from "react-intl";
import { Button } from "@folio/stripes/components";
import { getAccessibilityTranslation } from "../utils/getAccessibilityTranslation";
import { useOnixCodetableByNumber } from "../api/onixService";
import { ONIX_TABLES } from "../utils/onixTables";
import { onixLabel } from "../utils/onixLabel";
import { usePermissions } from "./usePermissions";
import { useDetailParam } from "../utils/detailParam";
import { buildDateAuditColumns } from "../utils/dateAuditColumns";
export var useProductTable = function (onRowClick) {
    var intl = useIntl();
    var formatMessage = intl.formatMessage;
    var history = useHistory();
    var _a = usePermissions(), isAgencyWorker = _a.isAgencyWorker, isSuperUser = _a.isSuperUser;
    var canAssignProductNumber = isAgencyWorker || isSuperUser;
    var updateDetailParam = useDetailParam().updateDetailParam;
    var formTable = useOnixCodetableByNumber(ONIX_TABLES.PRODUCT_FORM).data;
    var publishingStatusTable = useOnixCodetableByNumber(ONIX_TABLES.PUBLISHING_STATUS).data;
    var handleNameClick = useCallback(function (item) {
        onRowClick(item);
    }, [onRowClick]);
    var handleAssignProductNumber = useCallback(function (item) {
        if (!item.id)
            return;
        history.push("/bragency/product-numbers/assign/".concat(item.id));
    }, [history]);
    var handleRelatedProductClick = useCallback(function (targetId) {
        if (!targetId)
            return;
        updateDetailParam(targetId);
    }, [updateDetailParam]);
    var renderTitleData = useCallback(function (item) {
        var secondLineParts = [];
        if (item.partNumber) {
            secondLineParts.push("".concat(item.partNumber, "."));
        }
        if (item.partName) {
            secondLineParts.push(item.partName);
        }
        var secondLine = secondLineParts.join(" – ");
        return (React.createElement("div", null,
            React.createElement("div", null, item.mainTitle || ""),
            secondLine && (React.createElement("div", { className: "titleDataSecondaryLine" }, secondLine))));
    }, []);
    var renderRelatedProducts = useCallback(function (item) {
        var _a;
        var related = (_a = item.relatedProducts) !== null && _a !== void 0 ? _a : [];
        if (related.length === 0) {
            return "";
        }
        return (React.createElement("div", { className: "relatedProductsButtons" }, related.map(function (relation) { return (React.createElement(Button, { key: relation.targetId, buttonStyle: "default", marginBottom0: true, onClick: function () { return handleRelatedProductClick(relation.targetId); } }, onixLabel(formTable, relation.form))); })));
    }, [formTable, handleRelatedProductClick]);
    var columns = useMemo(function () { return __spreadArray([
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.titleData",
            }),
            key: "mainTitle",
            sortable: true,
            clickable: true,
            onClick: handleNameClick,
            cellRenderer: renderTitleData,
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.firstContributor",
            }),
            key: "firstContributor",
            sortable: true,
            cellRenderer: function (item) { return item.firstContributor || ""; },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.editionNumber",
            }),
            key: "editionNumber",
            sortable: false,
            width: 110,
            cellRenderer: function (item) {
                return item.editionNumber != null ? String(item.editionNumber) : "";
            },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.publisher",
            }),
            key: "publisher",
            sortable: true,
            cellRenderer: function (item) { return item.publisher || ""; },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.form",
            }),
            key: "form",
            sortable: true,
            cellRenderer: function (item) { return onixLabel(formTable, item.form); },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.productNumber",
            }),
            key: "productNumber",
            sortable: true,
            cellRenderer: function (item) {
                if (item.fullFormattedProductNumber) {
                    return item.fullFormattedProductNumber;
                }
                if (!canAssignProductNumber) {
                    return "";
                }
                return (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return handleAssignProductNumber(item); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.product.action.addProductNumber" })));
            },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.publishingDate",
            }),
            key: "publishingDate",
            sortable: true,
            cellRenderer: function (item) { return item.publishingDate || ""; },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.accessibility",
            }),
            key: "accessibility",
            sortable: true,
            cellRenderer: function (item) {
                return getAccessibilityTranslation(intl, item.accessibility) || "";
            },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.publishingStatus",
            }),
            key: "publishingStatus",
            sortable: false,
            cellRenderer: function (item) {
                return onixLabel(publishingStatusTable, item.publishingStatus);
            },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.product.table.columns.relatedProducts",
            }),
            key: "relatedProducts",
            sortable: false,
            cellRenderer: renderRelatedProducts,
        }
    ], buildDateAuditColumns(formatMessage, "ui-br-agency.product.table.columns"), true); }, [
        formatMessage,
        intl,
        handleNameClick,
        renderTitleData,
        renderRelatedProducts,
        formTable,
        publishingStatusTable,
        canAssignProductNumber,
        handleAssignProductNumber,
    ]);
    return {
        columns: columns,
    };
};
